import React from "react";
import { MapPin, ExternalLink, ChevronDown, Globe, Mail, Phone } from "lucide-react";
import { PanelShell, type PanelMeta } from "./PanelShell";

/** Normalise a raw Instagram value to a full profile URL.
 *  Handles: full URLs, @username, bare username. */
function toInstagramUrl(raw: string): string {
  const trimmed = raw.trim();
  // Already a full URL
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  // Strip leading @ if present
  const username = trimmed.replace(/^@/, "");
  return `https://www.instagram.com/${username}`;
}

/** Display label: strip protocol + www, strip trailing slash. */
function instagramLabel(raw: string): string {
  return raw
    .trim()
    .replace(/^https?:\/\/(www\.)?instagram\.com\/?/i, "")
    .replace(/^@/, "")
    .replace(/\/$/, "") || raw;
}

type MapCoords = { lat: number; lon: number } | null;

type CompanyContact = {
  Company_Number: string;
  Company_Name: string;
  Website?: string | null;
  Domain?: string | null;
  Email?: string | null;
  Phone?: string | null;
  Twitter?: string | null;
  LinkedIn?: string | null;
  Facebook?: string | null;
  Instagram?: string | null;
  YouTube?: string | null;
  AI_Confidence?: string | null;
  Status?: string | null;
  Found_At?: string | null;
  Updated_At?: string | null;
};

type WherePanelProps = {
  panel: PanelMeta;
  headerColor: string;
  layoutEdit: boolean;
  reportMode?: boolean;
  fullAddress?: string | null;
  shortAddress?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  postTown?: string | null;
  county?: string | null;
  postcode?: string | null;
  countryRegion?: string | null;
  mapCoords: MapCoords;
  mapLoading: boolean;
  mapError: boolean;
  mapEmbedUrl?: string | null;
  staticMapUrls?: string[] | null;
  mapSearchQuery: string;
  setMapLoading: (value: boolean) => void;
  setMapError: (value: boolean) => void;
  contacts?: CompanyContact[] | null;
  contactsLoading?: boolean;
};

export function WherePanel({
  panel,
  headerColor,
  layoutEdit,
  reportMode = false,
  fullAddress,
  shortAddress,
  addressLine1,
  addressLine2,
  postTown,
  county,
  postcode,
  countryRegion,
  mapCoords,
  mapLoading,
  mapError,
  mapEmbedUrl,
  staticMapUrls,
  mapSearchQuery,
  setMapLoading,
  setMapError,
  contacts,
  contactsLoading = false,
}: WherePanelProps) {
  const [reportMapIndex, setReportMapIndex] = React.useState(0);
  const [showAllContacts, setShowAllContacts] = React.useState(false);
  const reportMapSrc = staticMapUrls?.[reportMapIndex] ?? null;

  React.useEffect(() => {
    setReportMapIndex(0);
  }, [staticMapUrls]);

  // Primary contact is the first one (sorted by Updated_At DESC from backend)
  const primaryContact = contacts && contacts.length > 0 ? contacts[0] : null;
  const hasMultipleContacts = contacts && contacts.length > 1;

  const renderContactBadge = (confidence?: string | null, status?: string | null) => {
    if (!confidence && !status) return null;
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] bg-slate-100 text-slate-600">
        {confidence && <span className="capitalize">{confidence}</span>}
        {confidence && status && <span>·</span>}
        {status && <span className="capitalize">{status}</span>}
      </span>
    );
  };

  const renderContactInfo = (contact: CompanyContact, isAdditional = false) => {
    const websiteUrl = contact.Website || (contact.Domain ? `https://${contact.Domain}` : null);
    const hasAnyContact = websiteUrl || contact.Email || contact.Phone || 
      contact.Twitter || contact.LinkedIn || contact.Instagram;

    if (!hasAnyContact) return null;

    return (
      <div key={contact.Updated_At || Math.random()} className={`${isAdditional ? 'pt-2 border-t border-slate-200' : ''}`}>
        {isAdditional && (
          <div className="text-[9px] text-slate-400 uppercase tracking-wide mb-2">
            Additional source {contact.Updated_At ? `· ${new Date(contact.Updated_At).toLocaleDateString()}` : ''}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 text-[11px] text-slate-600">
          {/* Website */}
          {websiteUrl && (
            <div>
              <span className="uppercase text-[10px] tracking-wide text-slate-400 inline-flex items-center gap-1">
                <Globe className="h-3 w-3" />
                Website
              </span>
              <div>
                <a
                  href={websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                >
                  {contact.Website || contact.Domain}
                  <ExternalLink className="h-2.5 w-2.5" />
                </a>
              </div>
            </div>
          )}
          {/* Email */}
          {contact.Email && (
            <div>
              <span className="uppercase text-[10px] tracking-wide text-slate-400 inline-flex items-center gap-1">
                <Mail className="h-3 w-3" />
                Email
              </span>
              <div>
                <a
                  href={`mailto:${contact.Email}`}
                  className="text-slate-700 hover:text-slate-900 hover:underline"
                >
                  {contact.Email}
                </a>
              </div>
            </div>
          )}
          {/* Phone */}
          {contact.Phone && (
            <div>
              <span className="uppercase text-[10px] tracking-wide text-slate-400 inline-flex items-center gap-1">
                <Phone className="h-3 w-3" />
                Phone
              </span>
              <div>
                <a
                  href={`tel:${contact.Phone}`}
                  className="text-slate-700 hover:text-slate-900 hover:underline"
                >
                  {contact.Phone}
                </a>
              </div>
            </div>
          )}
          {/* LinkedIn */}
          {contact.LinkedIn && (
            <div>
              <span className="uppercase text-[10px] tracking-wide text-slate-400 inline-flex items-center gap-1">
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="#0A66C2">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                LinkedIn
              </span>
              <div>
                <a
                  href={contact.LinkedIn}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-700 hover:text-[#0A66C2] hover:underline inline-flex items-center gap-1 break-all transition-colors"
                >
                  {contact.LinkedIn.replace(/^https?:\/\/(www\.)?/, '')}
                  <ExternalLink className="h-2.5 w-2.5 flex-shrink-0" />
                </a>
              </div>
            </div>
          )}
          {/* X (Twitter) */}
          {contact.Twitter && (
            <div>
              <span className="uppercase text-[10px] tracking-wide text-slate-400 inline-flex items-center gap-1">
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                X
              </span>
              <div>
                <a
                  href={contact.Twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-700 hover:text-black dark:hover:text-white hover:underline inline-flex items-center gap-1 break-all transition-colors"
                >
                  {contact.Twitter.replace(/^https?:\/\/(www\.)?/, '')}
                  <ExternalLink className="h-2.5 w-2.5 flex-shrink-0" />
                </a>
              </div>
            </div>
          )}
          {/* Instagram */}
          {contact.Instagram && (
            <div>
              <span className="uppercase text-[10px] tracking-wide text-slate-400 inline-flex items-center gap-1">
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="url(#instagram-gradient)">
                  <defs>
                    <linearGradient id="instagram-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#FED373"/>
                      <stop offset="15%" stopColor="#F15245"/>
                      <stop offset="30%" stopColor="#D92E7F"/>
                      <stop offset="50%" stopColor="#9B36B7"/>
                      <stop offset="85%" stopColor="#515ECF"/>
                    </linearGradient>
                  </defs>
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
                Instagram
              </span>
              <div>
                <a
                  href={toInstagramUrl(contact.Instagram)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-700 hover:text-[#E4405F] hover:underline inline-flex items-center gap-1 break-all transition-colors"
                >
                  {instagramLabel(contact.Instagram)}
                  <ExternalLink className="h-2.5 w-2.5 flex-shrink-0" />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPlaceholderContact = () => {
    return (
      <div className="grid grid-cols-2 gap-3 text-[11px] text-slate-600">
        <div>
          <span className="uppercase text-[10px] tracking-wide text-slate-400 inline-flex items-center gap-1">
            <Globe className="h-3 w-3" />
            Website
          </span>
          <div className="text-slate-500">—</div>
        </div>
        <div>
          <span className="uppercase text-[10px] tracking-wide text-slate-400 inline-flex items-center gap-1">
            <Mail className="h-3 w-3" />
            Email
          </span>
          <div className="text-slate-500">—</div>
        </div>
        <div>
          <span className="uppercase text-[10px] tracking-wide text-slate-400 inline-flex items-center gap-1">
            <Phone className="h-3 w-3" />
            Phone
          </span>
          <div className="text-slate-500">—</div>
        </div>
        <div>
          <span className="uppercase text-[10px] tracking-wide text-slate-400 inline-flex items-center gap-1">
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="#0A66C2">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
            LinkedIn
          </span>
          <div className="text-slate-500">—</div>
        </div>
        <div>
          <span className="uppercase text-[10px] tracking-wide text-slate-400 inline-flex items-center gap-1">
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            X
          </span>
          <div className="text-slate-500">—</div>
        </div>
        <div>
          <span className="uppercase text-[10px] tracking-wide text-slate-400 inline-flex items-center gap-1">
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="url(#instagram-gradient-placeholder)">
              <defs>
                <linearGradient id="instagram-gradient-placeholder" x1="0%" y1="100%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#FED373"/>
                  <stop offset="15%" stopColor="#F15245"/>
                  <stop offset="30%" stopColor="#D92E7F"/>
                  <stop offset="50%" stopColor="#9B36B7"/>
                  <stop offset="85%" stopColor="#515ECF"/>
                </linearGradient>
              </defs>
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
            </svg>
            Instagram
          </span>
          <div className="text-slate-500">—</div>
        </div>
      </div>
    );
  };

  return (
    <PanelShell panel={panel} headerColor={headerColor} layoutEdit={layoutEdit} reportMode={reportMode}>
      <div
        className={`${reportMode ? "mt-2 gap-2" : "mt-3 gap-3"} flex flex-col text-[11px] text-slate-600 h-full min-h-0`}
        suppressHydrationWarning={reportMode}
      >
        <div className="shrink-0">
          <span className="uppercase text-[10px] tracking-wide text-slate-400">Registered address</span>
          <div className="text-slate-700">{fullAddress || "—"}</div>
        </div>
        <div className={`grid grid-cols-2 ${reportMode ? "gap-2" : "gap-3"} text-[11px] text-slate-600 shrink-0`}>
          <div>
            <span className="uppercase text-[10px] tracking-wide text-slate-400">Address line 1</span>
            <div className="text-slate-700">{addressLine1 || "—"}</div>
          </div>
          <div>
            <span className="uppercase text-[10px] tracking-wide text-slate-400">Address line 2</span>
            <div className="text-slate-700">{addressLine2 || "—"}</div>
          </div>
          <div>
            <span className="uppercase text-[10px] tracking-wide text-slate-400">Post town</span>
            <div className="text-slate-700">{postTown || "—"}</div>
          </div>
          <div>
            <span className="uppercase text-[10px] tracking-wide text-slate-400">County</span>
            <div className="text-slate-700">{county || "—"}</div>
          </div>
          <div>
            <span className="uppercase text-[10px] tracking-wide text-slate-400">Postcode</span>
            <div className="text-slate-700">{postcode || "—"}</div>
          </div>
          <div>
            <span className="uppercase text-[10px] tracking-wide text-slate-400">Country / Region</span>
            <div className="text-slate-700">{countryRegion || "—"}</div>
          </div>
        </div>
        <div
          className={`rounded-md border border-slate-200 bg-white overflow-hidden min-h-[160px] ${
            reportMode
              ? "report-map report-avoid-break mt-1 max-h-[60mm]"
              : "flex-1"
          }`}
        >
          {mapCoords ? (
            <div className="relative">
              {!reportMode && mapLoading && (
                <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-400 bg-white/70">
                  Loading map...
                </div>
              )}
              {!reportMode && mapError && (
                <div className="absolute inset-0 flex items-center justify-center text-xs text-amber-600 bg-white/70">
                  Map failed to load
                </div>
              )}
              {reportMode ? (
                mapError ? (
                  <div className="h-full min-h-[260px] flex flex-col items-center justify-center gap-2 text-xs text-slate-400">
                    <span>Map unavailable</span>
                    <span className="text-[10px] text-slate-500">{shortAddress || fullAddress || "—"}</span>
                  </div>
                ) : reportMapSrc ? (
                  <img
                    src={reportMapSrc}
                    alt="Company location map"
                    className="w-full h-full min-h-[260px] object-cover"
                    loading="eager"
                    onLoad={() => {
                      setMapError(false);
                      setMapLoading(false);
                    }}
                    onError={() => {
                      if (staticMapUrls && reportMapIndex < staticMapUrls.length - 1) {
                        setReportMapIndex((prev) => prev + 1);
                      } else {
                        setMapError(true);
                        setMapLoading(false);
                      }
                    }}
                  />
                ) : (
                  <div className="h-full min-h-[260px] flex items-center justify-center text-xs text-slate-400">
                    Map unavailable
                  </div>
                )
              ) : (
                <iframe
                  title="Company location map"
                  src={mapEmbedUrl ?? undefined}
                  className="w-full h-full min-h-[260px]"
                  scrolling="no"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  onLoad={() => {
                    setMapError(false);
                    setMapLoading(false);
                  }}
                  onError={() => {
                    setMapError(true);
                    setMapLoading(false);
                  }}
                />
              )}
            </div>
          ) : (
            <div className="h-full min-h-[260px] flex items-center justify-center text-xs text-slate-400">
              No coordinates available
            </div>
          )}
        </div>
        {!reportMode && (
          <>
            <div className="flex flex-wrap items-center gap-2 text-[10px] shrink-0">
              <span className="inline-flex items-center gap-1 text-slate-500">
                <MapPin className="h-3 w-3" />
                {shortAddress || "Address unavailable"}
              </span>
              {mapCoords && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="text-slate-500">
                    <span className="text-slate-400">Latitude:</span> {mapCoords.lat}
                  </span>
                  <span className="text-slate-300">·</span>
                  <span className="text-slate-500">
                    <span className="text-slate-400">Longitude:</span> {mapCoords.lon}
                  </span>
                </>
              )}
            </div>
            <div className="shrink-0">
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">Contact</div>
              {contactsLoading ? (
                <div className="text-[11px] text-slate-500">Loading contact details...</div>
              ) : primaryContact ? (
                <div className="flex flex-col gap-2">
                  {renderContactInfo(primaryContact)}
                  {hasMultipleContacts && !showAllContacts && (
                    <button
                      onClick={() => setShowAllContacts(true)}
                      className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 hover:underline self-start"
                    >
                      <ChevronDown className="h-3 w-3" />
                      Show {contacts!.length - 1} more source{contacts!.length - 1 > 1 ? 's' : ''}
                    </button>
                  )}
                  {showAllContacts && contacts!.slice(1).map((contact) => renderContactInfo(contact, true))}
                </div>
              ) : (
                renderPlaceholderContact()
              )}
            </div>
          </>
        )}
      </div>
    </PanelShell>
  );
}
